'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import {
  ShoppingCart, Package, Users, TrendingUp, AlertTriangle,
  Truck, CreditCard, ArrowRight, BarChart2
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface DashboardStats {
  todaySales: number;
  todayTransactions: number;
  weekSales: number;
  lowStockCount: number;
  totalCustomerCredit: number;
  pendingOrders: number;
  totalSupplierOwed: number;
}

interface LowStockItem {
  inventory_id: number;
  product_name: string;
  current_stock: number;
  min_stock_alert: number;
  supplier_name: string;
}

interface RecentSale {
  sale_id: number;
  total_amount: number;
  payment_method: string;
  customer_name: string | null;
  sale_date: string;
  created_at: string;
}

export default function RetailerDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    todaySales: 0, todayTransactions: 0, weekSales: 0,
    lowStockCount: 0, totalCustomerCredit: 0, pendingOrders: 0, totalSupplierOwed: 0,
  });
  const [lowStock, setLowStock] = useState<LowStockItem[]>([]);
  const [recentSales, setRecentSales] = useState<RecentSale[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const supabase = createClient();
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

      const [retailerRes, salesRes, inventoryRes, creditRes, ordersRes, supplierCreditRes] = await Promise.all([
        supabase.from('retailer_accounts').select('retailer_id').limit(1).maybeSingle(),
        supabase.from('retailer_pos_sales').select('*').gte('sale_date', weekAgo),
        supabase.from('retailer_inventory').select('*'),
        supabase.from('retailer_customer_credit').select('total_credit'),
        supabase.from('retailer_supplier_orders').select('order_status').neq('order_status', 'Delivered').neq('order_status', 'Cancelled'),
        supabase.from('retailer_supplier_credit').select('total_owed'),
      ]);

      const allSales = salesRes.data || [];
      const todaySalesData = allSales.filter(s => s.sale_date === today);
      const inventory = inventoryRes.data || [];
      const lowStockItems = inventory.filter((i: LowStockItem) => i.current_stock <= i.min_stock_alert);

      setStats({
        todaySales: todaySalesData.reduce((sum, s) => sum + (s.total_amount || 0), 0),
        todayTransactions: todaySalesData.length,
        weekSales: allSales.reduce((sum, s) => sum + (s.total_amount || 0), 0),
        lowStockCount: lowStockItems.length,
        totalCustomerCredit: (creditRes.data || []).reduce((sum: number, c: { total_credit: number }) => sum + (c.total_credit || 0), 0),
        pendingOrders: (ordersRes.data || []).length,
        totalSupplierOwed: (supplierCreditRes.data || []).reduce((sum: number, c: { total_owed: number }) => sum + (c.total_owed || 0), 0),
      });

      setLowStock(lowStockItems.slice(0, 5));
      setRecentSales(allSales.filter(s => s.sale_date === today).slice(0, 5));
      setLoading(false);
    };
    fetchData();
  }, []);

  const metricCards = [
    {
      label: 'مبيعات اليوم', labelFr: "Ventes aujourd\'hui",
      value: `${stats.todaySales.toFixed(2)} MAD`,
      sub: `${stats.todayTransactions} معاملة`,
      icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50',
      href: '/retailer/sales-reports',
    },
    {
      label: 'مبيعات الأسبوع', labelFr: 'Ventes semaine',
      value: `${stats.weekSales.toFixed(2)} MAD`,
      sub: '7 أيام الأخيرة',
      icon: BarChart2, color: 'text-blue-600', bg: 'bg-blue-50',
      href: '/retailer/sales-reports',
    },
    {
      label: 'تنبيهات المخزون', labelFr: 'Alertes stock',
      value: stats.lowStockCount.toString(),
      sub: 'منتج ناقص',
      icon: AlertTriangle, color: stats.lowStockCount > 0 ? 'text-amber-600' : 'text-muted-foreground', bg: stats.lowStockCount > 0 ? 'bg-amber-50' : 'bg-muted',
      href: '/retailer/inventory',
    },
    {
      label: 'كريدي الزبائن', labelFr: 'Crédit clients',
      value: `${stats.totalCustomerCredit.toFixed(2)} MAD`,
      sub: 'إجمالي الديون',
      icon: Users, color: 'text-purple-600', bg: 'bg-purple-50',
      href: '/retailer/customer-credit',
    },
    {
      label: 'طلبيات جارية', labelFr: 'Commandes en cours',
      value: stats.pendingOrders.toString(),
      sub: 'قيد التنفيذ',
      icon: Truck, color: 'text-sky-600', bg: 'bg-sky-50',
      href: '/retailer/supplier-orders',
    },
    {
      label: 'مستحق للموردين', labelFr: 'Dû fournisseurs',
      value: `${stats.totalSupplierOwed.toFixed(2)} MAD`,
      sub: 'إجمالي الكريدي',
      icon: CreditCard, color: 'text-rose-600', bg: 'bg-rose-50',
      href: '/retailer/supplier-credit',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-foreground">لوحة التحكم — Tableau de Bord</h1>
        <p className="text-sm text-muted-foreground mt-0.5">نظرة عامة على أداء المحل اليوم</p>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          href="/retailer/pos"
          className="flex items-center gap-3 p-4 bg-emerald-600 rounded-xl text-white hover:bg-emerald-700 transition-colors"
        >
          <ShoppingCart size={22} />
          <div>
            <p className="font-bold text-sm">فتح الكاشير</p>
            <p className="text-xs opacity-80">Ouvrir la caisse</p>
          </div>
          <ArrowRight size={16} className="ml-auto" />
        </Link>
        <Link
          href="/retailer/smart-order"
          className="flex items-center gap-3 p-4 bg-card border border-border rounded-xl hover:bg-muted transition-colors"
        >
          <Package size={22} className="text-emerald-600" />
          <div>
            <p className="font-bold text-sm text-foreground">طلب ذكي</p>
            <p className="text-xs text-muted-foreground">Commande intelligente</p>
          </div>
          <ArrowRight size={16} className="ml-auto text-muted-foreground" />
        </Link>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {metricCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.label}
              href={card.href}
              className="bg-card border border-border rounded-xl p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center`}>
                  <Icon size={17} className={card.color} />
                </div>
              </div>
              <p className="text-xl font-bold text-foreground">{card.value}</p>
              <p className="text-xs font-medium text-foreground mt-0.5">{card.label}</p>
              <p className="text-2xs text-muted-foreground">{card.sub}</p>
            </Link>
          );
        })}
      </div>

      {/* Two columns: low stock + recent sales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low stock alerts */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <h3 className="font-semibold text-sm text-foreground">تنبيهات المخزون</h3>
            </div>
            <Link href="/retailer/inventory" className="text-xs text-emerald-600 hover:underline">
              عرض الكل
            </Link>
          </div>
          {lowStock.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">✅ المخزون بخير — Stock OK</p>
          ) : (
            <div className="space-y-2">
              {lowStock.map((item) => (
                <div key={item.inventory_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-xs font-medium text-foreground">{item.product_name}</p>
                    <p className="text-2xs text-muted-foreground">{item.supplier_name}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-bold ${item.current_stock === 0 ? 'text-danger' : 'text-amber-600'}`}>
                      {item.current_stock} / {item.min_stock_alert}
                    </span>
                    <p className="text-2xs text-muted-foreground">متبقي / min</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent sales */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShoppingCart size={16} className="text-emerald-600" />
              <h3 className="font-semibold text-sm text-foreground">مبيعات اليوم</h3>
            </div>
            <Link href="/retailer/sales-reports" className="text-xs text-emerald-600 hover:underline">
              عرض الكل
            </Link>
          </div>
          {recentSales.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">لا توجد مبيعات اليوم</p>
          ) : (
            <div className="space-y-2">
              {recentSales.map((sale) => (
                <div key={sale.sale_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-xs font-medium text-foreground">
                      {sale.customer_name || 'زبون عادي'}
                    </p>
                    <p className="text-2xs text-muted-foreground">{sale.payment_method}</p>
                  </div>
                  <span className="text-xs font-bold text-emerald-600">
                    {sale.total_amount.toFixed(2)} MAD
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
